// utils/garbageCollector.ts

// Extended TypeScript definitions for GC methods
declare global {
  interface Window {
    gc?: () => void;
    Components?: {
      utils?: {
        forceGC?: () => void;
      };
    };
  }

  interface Performance {
    memory?: {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
  }
}

// Method 1: Force GC by allocating and releasing large amounts of memory
export function triggerGarbageCollection(): boolean {
  if (window.gc) {
    // Explicit GC call (available in Chrome with flags)
    window.gc();
    console.log("🧹 Explicit GC triggered");
    return true;
  } else {
    // Fallback: Force GC through memory pressure
    return forceGCMemoryPressure();
  }
}

// Method 2: Create memory pressure to force GC
function forceGCMemoryPressure(): boolean {
  try {
    // Create and release large arrays to encourage GC
    const iterations = 10;
    const largeArraySize = 1000000; // 1 million items

    for (let i = 0; i < iterations; i++) {
      // Allocate large memory chunks
      const largeArray = new Array(largeArraySize);
      for (let j = 0; j < largeArraySize; j++) {
        largeArray[j] = new Object();
      }

      // Immediately release references
      largeArray.length = 0;
    }

    // Clear any global references
    if (globalThis.gc) {
      (globalThis as any).gc();
    }

    console.log("🧹 Memory pressure GC triggered");
    return true;
  } catch (error) {
    console.warn("Memory pressure GC failed:", error);
    return false;
  }
}

// Method 3: Use performance.memory to monitor and trigger GC
export function aggressiveGarbageCollection(): boolean {
  const gcMethods: (() => void)[] = [];

  // Chrome/Edge explicit GC
  if (window.gc) {
    gcMethods.push(() => window.gc!());
  }

  // Firefox explicit GC
  if (window.Components?.utils?.forceGC) {
    gcMethods.push(() => window.Components!.utils!.forceGC!());
  }

  // Node.js/Electron GC
  if ((globalThis as any).gc) {
    gcMethods.push(() => (globalThis as any).gc());
  }

  // Try all available GC methods
  gcMethods.forEach((method) => {
    try {
      method();
      console.log("🧹 GC method executed");
    } catch (error) {
      console.warn("GC method failed:", error);
    }
  });

  // Always try memory pressure method as fallback
  if (gcMethods.length === 0) {
    // return forceGCMemoryPressure();
  }

  return gcMethods.length > 0;
}

// Method 4: Safe memory pressure (less aggressive)
export function safeGarbageCollection(): boolean {
  try {
    // Less aggressive approach - smaller arrays
    const iterations = 3;
    const arraySize = 100000; // 100k items instead of 1M

    for (let i = 0; i < iterations; i++) {
      const array = new Array(arraySize);
      for (let j = 0; j < arraySize; j++) {
        array[j] = { index: j };
      }
      array.length = 0;
    }

    console.log("🧹 Safe GC triggered");
    return true;
  } catch (error) {
    console.warn("Safe GC failed:", error);
    return false;
  }
}

// Method 5: Memory usage monitoring
export function getMemoryUsage(): {
  usedMB: number;
  totalMB: number;
  limitMB: number;
} | null {
  if (performance.memory) {
    return {
      usedMB: Math.round(performance.memory.usedJSHeapSize / 1048576),
      totalMB: Math.round(performance.memory.totalJSHeapSize / 1048576),
      limitMB: Math.round(performance.memory.jsHeapSizeLimit / 1048576),
    };
  }
  return null;
}

// Method 6: Smart GC based on memory usage
export function smartGarbageCollection(thresholdMB: number = 500): boolean {
  const memory = getMemoryUsage();

  if (memory && memory.usedMB > thresholdMB) {
    console.log(
      `🚨 High memory usage detected: ${memory.usedMB}MB, triggering GC`
    );
    return aggressiveGarbageCollection();
  } else if (memory) {
    console.log(
      `💾 Memory usage: ${memory.usedMB}MB (below ${thresholdMB}MB threshold)`
    );
    return safeGarbageCollection();
  }

  // Fallback to safe GC if memory API not available
  return safeGarbageCollection();
}

// Method 7: Check if memory API is available
export function isMemoryAPIAvailable(): boolean {
  return !!performance.memory;
}

// Method 8: Get memory usage percentage
export function getMemoryUsagePercentage(): number | null {
  const memory = getMemoryUsage();
  if (memory && memory.limitMB > 0) {
    return Math.round((memory.usedMB / memory.limitMB) * 100);
  }
  return null;
}
